/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Center,
  Flex,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCheck,
  IconMicrophoneFilled,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlayerStopFilled,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Loader } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { getUrl, uploadData } from "aws-amplify/storage";
import { COLORS } from "@/utils/GlobalStyles";
import { useUserProvider } from "@/utils/UserProvider";
import {
  checkIfMaxTemplates,
  checkIfMaxTranscriptions,
} from "@/utils/checkSubscriptionUsage";
import {
  AudioDatabase,
  RECORD_TYPES,
  appShowNotification,
  onSaveBlobToIndexDB,
} from "@/utils/utils";
import QuickRecordUploadFailedModal from "../QuickRecord/UploadFailedModal";
import PayWallModal from "./PayWallModal";
import ConfirmCancelRecordDialog from "./Recording/ConfirmCancelRecordDialog";
import useCreateRecording from "@/hooks/useCreateRecording";

type createRecordProps = {
  modalOpened: boolean;
  recordType: string;
  patientInfo: any;
  caseInfo: any;
  useTemplate: any;
  isShowSelectType: boolean;
  setModalOpened: (value: boolean) => void;
  fetchDetails: () => void;
};

const CreateRecordModal = (props: createRecordProps) => {
  const { amplifyUser, revcatUser, mixpanel } = useUserProvider();
  const { handleCreateRecording } = useCreateRecording();

  const {
    modalOpened,
    setModalOpened,
    caseInfo,
    useTemplate,
    patientInfo,
    isShowSelectType,
    fetchDetails,
  } = props;

  const [showPayWall, setShowPayWall] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const shouldUploadRecordRef = useRef(false);

  const [type, setType] = useState(RECORD_TYPES.HISTORY);
  const selectedTypeRef = useRef<any>([]);
  const [recordTypes, setRecordTypes] = useState<any>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorder = useRef<any>(null);
  const startTimeRef = useRef<any>([]);
  const audioChunks = useRef<any>([]);
  const barsRef = useRef<any>(null);
  const timer = useRef<any>(null);
  const isMobile = useMediaQuery("(max-width: 50em)");
  const [openedCancelRecord, setOpenedCancelRecord] = useState(false);
  const [openedReuploadModal, setOpenedReuploadModal] = useState(false);
  const [recordingItem, setRecordingItem] = useState(false);
  const fileInputRef = useRef<any>();
  const [error, setError] = useState<string>("");

  useEffect(() => {
    return () => {
      clearInterval(timer.current);
    };
  }, []);

  useEffect(() => {
    selectedTypeRef.current = type;
  }, [type]);

  useEffect(() => {
    if (modalOpened) {
      setError("");
      initRecordType();
    }
  }, [modalOpened]);

  const initRecordType = () => {
    const data = Object.keys(RECORD_TYPES);
    const lstType = data.map((type: any) => {
      return { value: type, label: _.capitalize(type.toLowerCase()) };
    });
    setRecordTypes(lstType);
    setType(RECORD_TYPES.HISTORY);
  };

  // Waveform
  const audioContext = useRef<any>(null);
  const analyser = useRef<any>(null);
  const dataArray = useRef<any>(null);
  const animationId = useRef<any>(null);
  const mediaStream = useRef<any>(null);

  useEffect(() => {
    if (recording) {
      const autoStopTimer = setTimeout(() => {
        stopRecording({ isUploadRecord: true });
      }, 2700000); // 45 minutes in ms
      return () => clearTimeout(autoStopTimer);
    }
  }, [recording]);

  const handleStartRecordingPress = async () => {
    if (useTemplate) {
      if (await checkIfMaxTemplates(revcatUser, amplifyUser)) {
        setShowPayWall(true);
        appShowNotification({
          type: "error",
          title:
            "You have reached the limit.\nPlease upgrade your subscription from your Profile",
        });
        return;
      }
      startRecording();
      return;
    }

    if (await checkIfMaxTranscriptions(revcatUser, amplifyUser)) {
      setShowPayWall(true);
      appShowNotification({
        type: "error",
        title:
          "You have reached the limit.\nPlease upgrade your subscription from your Profile",
      });
      return;
    }
    startRecording();
  };

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60)
      .toString()
      .padStart(2, "0");
    const seconds = (timeInSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  async function startRecording() {
    try {
      mediaStream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaRecorder.current = new MediaRecorder(mediaStream.current, {
        mimeType: "audio/webm",
      });

      setRecording(true);
      startTimeRef.current = Date.now();

      mediaRecorder.current.ondataavailable = (event: any) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        if (shouldUploadRecordRef.current == false) {
          if (mediaStream.current) {
            mediaStream.current
              .getTracks()
              .forEach((track: any) => track?.stop());
          }
          stopVisualizing();
          audioChunks.current = [];
          clearInterval(timer.current); // Clear the timer on stop
          setRecordingTime(0); // Reset the timer
          return;
        }
        const endTime = Date.now();
        const duration = (endTime - startTimeRef.current) / 1000; // in seconds
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        const objectToURL = URL.createObjectURL(audioBlob);
        const timestamp = new Date().toLocaleTimeString("en-US", {
          hour12: false,
        });
        const filename = (
          "Recording" +
          timestamp.replace(/\s/g, "") +
          ".webm"
        ).replace(/:\s*/g, "");

        // Create storage path in s3
        const storagePath =
          amplifyUser.id +
          "/" +
          patientInfo.lID +
          "/" +
          patientInfo.id +
          "/" +
          caseInfo.id +
          "/" +
          selectedTypeRef.current +
          "/" +
          filename;

        const newRecording = {
          name: filename,
          duration: duration,
          date: Date.now(),
          file: objectToURL,
          storagePath: storagePath,
          useTemplate: !!caseInfo.caseTemplateId,
          templateId: caseInfo.caseTemplateId ?? null,
          blob: audioBlob,
          caseId: caseInfo.id,
          patientId: patientInfo.id,
          patientLID: patientInfo.lID,
        };

        uploadRecording(newRecording);
        if (mediaStream.current) {
          mediaStream.current
            .getTracks()
            .forEach((track: any) => track?.stop());
        }
        audioChunks.current = [];
        clearInterval(timer.current); // Clear the timer on stop
        setRecordingTime(0); // Reset the timer
        stopVisualizing();
      };

      // drawWaveform(); // Start visualization
      initializeWaveformVisualization();

      mediaRecorder.current.start();
      setRecording(true);
      setIsPaused(false);

      // Start timer
      timer.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000); // Increment time every second
    } catch (error) {
      console.error("Error accessing microphone:", error);
    }
  }

  const initializeWaveformVisualization = () => {
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(mediaStream.current);
    analyser.current = audioContext.createAnalyser();
    analyser.current.fftSize = 64; // Number of bars (must be a power of 2)
    const bufferLength = analyser.current.frequencyBinCount;
    dataArray.current = new Uint8Array(bufferLength);

    source.connect(analyser.current);

    // Start visualizing
    drawWaveform();
  };

  const drawWaveform = () => {
    const barsContainer = barsRef.current;
    const bufferLength = dataArray.current.length;

    if (!barsContainer) return;

    // Create div elements for the bars
    if (!barsContainer.hasChildNodes()) {
      for (let i = 0; i < bufferLength; i++) {
        const bar = document.createElement("div");
        bar.style.backgroundColor = COLORS.primary;
        bar.style.width = "5px";
        bar.style.borderRadius = "10px";
        bar.style.transition = "height 0.1s ease";
        barsContainer.appendChild(bar);
      }
    }

    const bars = barsContainer.childNodes;
    const minHeight = 5;
    const maxHeight = 93;
    const renderFrame = () => {
      animationId.current = requestAnimationFrame(renderFrame);

      analyser.current.getByteFrequencyData(dataArray.current);

      for (let i = 0; i < bufferLength; i++) {
        let barHeight = (dataArray.current[i] / 64) * 100;
        if (barHeight > maxHeight) {
          barHeight = maxHeight;
        }
        if (barHeight < minHeight) {
          barHeight = minHeight;
        }
        bars[i].style.height = `${barHeight}px`;
      }
    };

    renderFrame();
  };

  const uploadRecording = async (recordingItem: any) => {
    try {
      setRecordingItem(recordingItem);
      setUploading(true);
      const identityId = (await fetchAuthSession()).identityId;
      const s3Put = uploadData({
        path: `private/${identityId}/${recordingItem.storagePath}`,
        data: recordingItem.blob,
        options: {
          metadata: {
            duration: recordingItem.duration,
            usetemplate: recordingItem.useTemplate,
            name: recordingItem.name?.split(".")[0],
            templateid: recordingItem.templateId,
            retention: amplifyUser.audioRetentionPeriod ?? 0,
          },
        },
      }); // lowercased metadata here because it gets lowercased once uploaded anyway
      await s3Put.result;
      const urlAudio = (
        await getUrl({
          path: `private/${identityId}/${recordingItem.storagePath}`,
          options: { validateObjectExistence: true },
        })
      ).url.toString();
      recordingItem["urlAudio"] = urlAudio;
      onSaveBlobToIndexDB(
        { ...recordingItem, failedToUpload: false },
        `${AudioDatabase.PREFIX_RECORD}${caseInfo.id}`,
      );
      // Create recording in Amplify (will sync to Supabase after transcription)
      try {
        console.log('[CREATE-RECORD-MODAL] Creating recording in Amplify via proper flow');
        await handleCreateRecording({
          name: recordingItem.name,
          duration: recordingItem.duration,
          storagePath: recordingItem.storagePath,
          useTemplate: recordingItem.useTemplate,
          caseId: caseInfo.id,
          templateId: recordingItem.templateId,
        });
        console.log('[CREATE-RECORD-MODAL] Recording created successfully in Amplify');
      } catch (e) {
        console.error('[CREATE-RECORD-MODAL] Failed to create recording in Amplify:', e);
      }
      if (recordingItem.useTemplate) {
        try {
          mixpanel.track("Use Template", {});
        } catch (e) {
          console.log(e);
        }
      }
      setTimeout(() => {
        setModalOpened(false);
        appShowNotification({
          type: "success",
          title: "Recording successfully uploaded!",
        });
        setUploading(false);
        setTimeout(() => {
          fetchDetails();
        }, 1000);
      }, 2000);
    } catch (error: any) {
      console.log(error.message);
      onSaveBlobToIndexDB(
        { ...recordingItem, failedToUpload: true },
        `${AudioDatabase.PREFIX_RECORD}${caseInfo.id}`,
      );
      setOpenedReuploadModal(true);
    } finally {
      setUploading(false);
    }
  };

  const onCancelRecord = () => {
    shouldUploadRecordRef.current = false;
    stopRecording({ isUploadRecord: false });
  };

  async function cancelRecording() {
    setOpenedCancelRecord(true);
  }

  // Pause recording and pause the timer
  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.pause();
      setIsPaused(true);
      clearInterval(timer.current); // Pause the timer
    }
  };

  // Resume recording and resume the timer
  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "paused") {
      mediaRecorder.current.resume();
      setIsPaused(false);
      timer.current = setInterval(() => {
        setRecordingTime((prevTime) => prevTime + 1);
      }, 1000); // Resume timer
    }
  };

  const stopVisualizing = () => {
    cancelAnimationFrame(animationId.current);
    const barsContainer = barsRef.current;
    if (barsContainer) {
      barsContainer.innerHTML = ""; // Clear bars
    }
  };

  // Stop recording and stop the timer
  const stopRecording = ({ isUploadRecord = true }) => {
    shouldUploadRecordRef.current = isUploadRecord;
    if (mediaRecorder.current) {
      mediaRecorder.current.stop();
      setRecording(false);
      setIsPaused(false);
      clearInterval(timer.current); // Clear the timer
    }
    if (audioContext.current) {
      audioContext.current.close();
    }
  };

  const getAudioDuration = (audioBlob: any, newRecording: any) => {
    const tempAudio = document.createElement("audio");
    tempAudio.style.display = "none";
    tempAudio.src = URL.createObjectURL(audioBlob);
    tempAudio.onloadedmetadata = () => {
      const fileRecordUpload = { ...newRecording };
      fileRecordUpload.duration = tempAudio.duration;
      uploadRecording(fileRecordUpload);
      document.removeChild(tempAudio);
    };

    tempAudio.onerror = () => {
      console.error("Error loading audio metadata.");
    };
  };

  const handleRecordUpload = async (event: any) => {
    const file = event.target.files[0]; // Get the selected file
    const allowedTypes = [
      "audio/mpeg",
      "audio/mp4",
      "audio/webm",
      "video/webm",
      "audio/x-m4a",
    ];
    try {
      if (file && allowedTypes.includes(file.type)) {
        setError("");
        const audioBlob = new Blob([file], { type: file.type });

        const objectToURL = URL.createObjectURL(audioBlob);
        const timestamp = new Date().toLocaleTimeString("en-US", {
          hour12: false,
        });
        const fileExtension = file.name.split(".").pop();
        const filename = (
          "Recording" +
          timestamp.replace(/\s/g, "") +
          `.${fileExtension}`
        ).replace(/:\s*/g, "");

        // Create storage path in s3
        const storagePath =
          amplifyUser.id +
          "/" +
          patientInfo.lID +
          "/" +
          patientInfo.id +
          "/" +
          caseInfo.id +
          "/" +
          selectedTypeRef.current +
          "/" +
          filename;
        const newRecording = {
          name: filename,
          duration: 1,
          date: Date.now(),
          file: objectToURL,
          storagePath: storagePath,
          useTemplate: caseInfo.caseTemplateId === null ? false : true,
          templateId: caseInfo.caseTemplateId,
          blob: audioBlob,
        };
        getAudioDuration(audioBlob, newRecording);
        // uploadRecording(newRecording);
      } else {
        setError("Please upload a .m4a, .webm, or .mp3 file.");
      }
    } catch (err) {
      console.error("Error uploading file:", err);
    }
  };

  return (
    <>
      <Modal
        opened={modalOpened}
        fullScreen={isMobile}
        centered
        closeOnEscape={false}
        onClose={() => setModalOpened(false)}
        title={recording ? "New recording" : "Create recording"}
        closeOnClickOutside={false}
        withCloseButton={!recording}
        id="modal-recording"
        size={"lg"}
      >
        <input
          type="file"
          accept=".m4a, .webm, .mp3"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleRecordUpload}
        />
        {uploading && (
          <Center py={"md"}>
            <Stack ta={"center"} justify="center" align="center">
              <Loader width={40} color={"#003554"} />
              <Text>Uploading</Text>
            </Stack>
          </Center>
        )}
        {!uploading && !recording && (
          <>
            <Center>
              <Tooltip withArrow label="Start record">
                <ActionIcon
                  onClick={handleStartRecordingPress}
                  variant="outline"
                  color=""
                  size="xl"
                  radius="xl"
                >
                  <IconMicrophoneFilled
                    style={{ width: "60%", height: "60%" }}
                    stroke={1.5}
                  />
                </ActionIcon>
              </Tooltip>
              <Tooltip withArrow label="Upload file record">
                <ActionIcon
                  ml={"md"}
                  variant="outline"
                  color=""
                  size="xl"
                  radius="xl"
                  onClick={() => {
                    fileInputRef.current.click();
                  }}
                >
                  <IconUpload
                    style={{ width: "60%", height: "60%" }}
                    stroke={1.5}
                  />
                </ActionIcon>
              </Tooltip>
            </Center>
            <Center mt={20} mb={20}>
              <Text fz={14} c={"red"}>
                {error}
              </Text>
            </Center>
          </>
        )}

        <Center display={recording ? "block" : "none"}>
          <Flex
            direction={"column"}
            align={"center"}
            flex={1}
            style={{ width: "100%" }}
          >
            <Paper
              bg={"#003554"}
              radius={"50%"}
              w={200}
              h={200}
              shadow="xl"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => {
                stopRecording({ isUploadRecord: true });
              }}
            >
              <IconPlayerStopFilled size={"80"} color="white" />
            </Paper>
            <Text w={150} fz={30} fw={800} ta={"center"} mt={15} mb={35}>
              {formatTime(recordingTime)}
            </Text>
            <Flex
              h={150}
              bg={"#F0F0F082"}
              justify={"center"}
              align={"center"}
              w={"100%"}
              px={10}
            >
              <Box
                ref={barsRef}
                style={{
                  width: `100%`,
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                  height: 93,
                  alignContent: "flex-start",
                  justifyContent: "space-between",
                  overflow: "hidden",
                }}
              ></Box>
            </Flex>
            <Box
              bg={"#E6E6E6"}
              h={82}
              mt={34}
              w={335}
              style={{
                display: "flex",
                borderRadius: "24px",
                justifyContent: "center",
                alignItems: "center",
                boxShadow: "0px 4px 4px 0px #00000033",
              }}
            >
              <Tooltip withArrow label="Delete">
                <ActionIcon
                  onClick={cancelRecording}
                  w={53}
                  h={56}
                  radius={"10"}
                  bg={"white"}
                  c={"#B70000"}
                  style={{
                    boxShadow: "0px 4px 4px 0px #0000001A",
                  }}
                >
                  <IconX size={30} />
                </ActionIcon>
              </Tooltip>
              <Box
                bg={"#E6E6E6"}
                h={108}
                w={110}
                mx={28}
                style={{
                  display: "flex",
                  borderRadius: "50%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Tooltip withArrow label={isPaused ? "Continue" : "Pause"}>
                  <ActionIcon
                    w={88}
                    h={90}
                    radius={"50%"}
                    bg={"white"}
                    c={"#000"}
                    onClick={isPaused ? resumeRecording : pauseRecording}
                  >
                    {isPaused ? (
                      <IconPlayerPlay
                        style={{ width: "60%", height: "60%" }}
                        stroke={1.5}
                      />
                    ) : (
                      <IconPlayerPause
                        style={{ width: "60%", height: "60%" }}
                        stroke={1.5}
                      />
                    )}
                  </ActionIcon>
                </Tooltip>
              </Box>
              <Tooltip withArrow label="Done">
                <ActionIcon
                  w={53}
                  h={56}
                  radius={"10"}
                  bg={"white"}
                  c={"#003554"}
                  style={{
                    boxShadow: "0px 4px 4px 0px #0000001A",
                  }}
                  onClick={() => {
                    stopRecording({ isUploadRecord: true });
                  }}
                >
                  <IconCheck size={30} color="#003554" />
                </ActionIcon>
              </Tooltip>
            </Box>
          </Flex>
        </Center>
        {!uploading && !recording && isShowSelectType && (
          <Select
            mt={40}
            w={250}
            mx={"auto"}
            data={recordTypes}
            value={type}
            onChange={(_value, option) => setType(option.value)}
          />
        )}
      </Modal>
      <ConfirmCancelRecordDialog
        openedModal={openedCancelRecord}
        setOpenedModal={setOpenedCancelRecord}
        onCancel={onCancelRecord}
      />
      <QuickRecordUploadFailedModal
        modalOpened={openedReuploadModal}
        onCancel={() => setOpenedReuploadModal(false)}
        onTryAgain={() => {
          setOpenedReuploadModal(false);
          setTimeout(() => {
            uploadRecording(recordingItem);
          }, 1000);
        }}
      />
      {amplifyUser && (
        <PayWallModal
          modalOpened={showPayWall}
          setModalOpened={setShowPayWall}
        />
      )}
    </>
  );
};

export default CreateRecordModal;
